import { PropertySearchResult, Property } from '../../../services/dtos/property';
import {
  buildURLObject, withQueryString, slugIt, QueryObject, URLObject,
  withCanonicalPageQueryString,
} from './common';
import {
  SERP_URL, LISTING_URL,
  BUY_PROPERTY_URL, BUY_PROPERTY_AS,
  BUILDING_SITES_URL, BUILDING_SITE_AS, BUILDING_SITE_URL,
  SOLD_PROPERTIES_URL, SOLD_PROPERTIES_AS,
  SERP_AS, GUIDE_URL, GUIDE_AS,
  SELL_PROPERTY_BY_LOCALITY_AND_TYPE_URL, SELL_PROPERTY_BY_LOCALITY_AND_TYPE_AS,
  SELL_PROPERTY_BY_LOCALITY_AND_AREA_URL, SELL_PROPERTY_BY_LOCALITY_AND_AREA_AS,
  SELL_PROPERTY_BY_LOCALITY_URL, SELL_PROPERTY_BY_LOCALITY_AS,
} from './paths';
import * as graphCMS from '../../../domain/graphcms';

export interface BuildSerpURLObject {
  province?: string | null,
  locality?: string | null,
  area?: string | null,
  p?: string | null,
  size_min?: string | null,
  size_max?: string | null,
  price_min?: string | null,
  price_max?: string | null,
  [key: string]: string,
}

export interface BuildListingURLObject {
  id: string,
  locality: string,
  slug: string,
  [key: string]: string,
}

export interface BuildSellPropertyByLocalityURLObject {
  locality: string,
  [key: string]: string,
}

export interface BuildSellPropertyByLocalityAndTypeURLObject {
  locality: string,
  type: graphCMS.PropertyType,
  [key: string]: string,
}

export interface BuildSellPropertyByLocalityAndAreaURLObject {
  locality: string,
  area: string,
  [key: string]: string,
}

export function buildListingURLObject(property: PropertySearchResult | Property, query: QueryObject = {}) {
  const id = property.id.toString();
  const { locality } = property;
  const slug = property.relativePath.match(/(\/vendita-case)\/([\w-]+)\/([\w0-9\-_]+)\/([0-9]+)/)[3];

  return buildURLObject(
    LISTING_URL,
    withCanonicalPageQueryString(`/vendita-case/${slugIt(locality)}/${slug}/${id}`, query),
    {
      ...query, id, locality: slugIt(locality), slug,
    },
  );
}

function buildSearchURLObject(path: string, as: string, {
  province,
  locality,
  area,
  ...rest
}: BuildSerpURLObject = {}): URLObject {
  const provinceSlug = province ? slugIt(province) : undefined;
  const localitySlug = locality ? slugIt(locality) : undefined;
  const areaSlug = area ? slugIt(area) : undefined;

  if (provinceSlug) {
    as += `/${provinceSlug}-provincia`;
  } else if (localitySlug) {
    as += `/${localitySlug}${areaSlug ? `/${areaSlug}` : ''}`;
  }

  const query: QueryObject = {
    ...rest,
  };

  if (provinceSlug) {
    query.province = provinceSlug;
  }

  if (!provinceSlug && localitySlug) {
    query.locality = localitySlug;
  }

  if (!provinceSlug && areaSlug) {
    query.area = areaSlug;
  }

  return buildURLObject(path, withCanonicalPageQueryString(as, { ...rest }), query);
}

export function buildSerpURLObject(serpUrlObject: BuildSerpURLObject = {}) {
  return buildSearchURLObject(SERP_URL, SERP_AS, serpUrlObject);
}

export function buildSoldPropertiesURLObject(serpUrlObject: BuildSerpURLObject = {}) {
  return buildSearchURLObject(SOLD_PROPERTIES_URL, SOLD_PROPERTIES_AS, serpUrlObject);
}

export function buildSellPropertyURLObject(locality?: string, query: QueryObject = {}) {
  return buildURLObject(
    SELL_PROPERTY_BY_LOCALITY_URL,
    withQueryString(locality ? `${SELL_PROPERTY_BY_LOCALITY_AS}/${slugIt(locality)}` : SELL_PROPERTY_BY_LOCALITY_AS, query),
    {
      locality: locality ? slugIt(locality) : undefined,
      ...query,
    },
  );
}

export function buildSellPropertyByLocalityAndTypeURLObject(
  { locality, type, ...rest }: BuildSellPropertyByLocalityURLObject,
) {
  return buildURLObject(
    SELL_PROPERTY_BY_LOCALITY_AND_TYPE_URL,
    withQueryString(`${SELL_PROPERTY_BY_LOCALITY_AND_TYPE_AS}-${type}/${slugIt(locality)}`, rest),
    { locality: slugIt(locality), type, ...rest },
  );
}

export function buildSellPropertyByLocalityAndAreaURLObject(
  { locality, area, ...rest }: BuildSellPropertyByLocalityAndAreaURLObject,
) {
  return buildURLObject(
    SELL_PROPERTY_BY_LOCALITY_AND_AREA_URL,
    withQueryString(`${SELL_PROPERTY_BY_LOCALITY_AND_AREA_AS}/${slugIt(locality)}/${slugIt(area)}`, rest),
    { locality: slugIt(locality), area: slugIt(area), ...rest },
  );
}

export function buildSellPropertyGuideURLObject(guideRef: Pick<graphCMS.Guide, 'id' | 'title' | 'alternateTitle'>, query: QueryObject = {}) {
  const { id, title, alternateTitle } = guideRef;

  return buildURLObject(
    `${GUIDE_URL}/${slugIt(alternateTitle || title)}/${id}`,
    withQueryString(`${GUIDE_AS}/${slugIt(alternateTitle || title)}/${id}`, query),
    query,
  );
}

export function buildBuyPropertyURLObject(query: QueryObject = {}) {
  return buildURLObject(BUY_PROPERTY_URL, withQueryString(BUY_PROPERTY_AS, query), query);
}

export function buildBuildingSitesURLObject(query: QueryObject = {}) {
  return buildURLObject(
    BUILDING_SITES_URL,
    withQueryString(BUILDING_SITE_AS, query),
    query,
  );
}

export function buildBuildingSiteURLObject({
  id, locality, slug, ...rest
}: QueryObject) {
  return buildURLObject(
    BUILDING_SITE_URL,
    withQueryString(`${BUILDING_SITE_AS}/${slugIt(locality)}/${slug}/${id}`, rest),
    {
      id,
      locality: slugIt(locality),
      slug,
      ...rest,
    },
  );
}
